import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { createQuestionnaireRequestAttachment, removeQuestionnaireRequestAttachment } from './apis'
import {
  postQuestionnaireArchive,
  postQuestionnaireUnArchive,
} from 'components/QuestionnaireRequestAutomated/api/processor'
import { existingAttachmentKey, sortAttachments } from 'components/QuestionnaireRequestAutomated/processor/utils'

const initialState = {
  statuses: [],
  currentStatus: '',
  id: null,
  formInstanceId: null,
  hashedId: null,
  propertyManagerId: null,
  requesterInfo: { name: '', company: '', email: '', phoneNumber: '' },
  propertyAddress: { address: '', unit: '' },
  buildingAddress: { city: '', state: '', zip: '' },
  buildingId: null,
  buildingUrl: '',
  buildingType: '',
  respondingFiles: [],
  questionnaireForm: { url: '', pages: [] },
  attachmentsMap: {},
  workflowType: null,
  paidAt: null,
  autoArchive: false
}

export const addRespondingFile = createAsyncThunk(
  'global/addRespondingFile',
  async ({ questionnaireRequestId, fileSandboxId, position }) => {
    const response = await createQuestionnaireRequestAttachment(questionnaireRequestId, fileSandboxId, position)
    const { id, ext, bytes, type, original_filename, url } = response.data
    return { id, ext, bytes, type, original_filename, url }
  }
)

// TODO: Remove multiple concurrent files removal
export const removeRespondingFile = createAsyncThunk(
  'global/removePdfFile',
  async ({ questionnaireRequestId, attachmentId, fileSandboxId }) => {
    await removeQuestionnaireRequestAttachment(questionnaireRequestId, attachmentId)
  }
)

export const archiveQuestionnaire = createAsyncThunk(
  'global/archiveQuestionnaire',
  async ({ questionnaireRequestId, purchaserToken }) => {
    const response = await postQuestionnaireArchive(questionnaireRequestId, purchaserToken)
    return response.data
  }
)

export const unarchiveQuestionnaire = createAsyncThunk(
  'global/unarchiveQuestionnaire',
  async ({ questionnaireRequestId, purchaserToken }) => {
    const response = await postQuestionnaireUnArchive(questionnaireRequestId, purchaserToken)
    return response.data
  }
)

const globalSlice = createSlice({
  name: 'global',
  initialState,
  reducers: {
    initialInfoGlobal: (
      state,
      {
        payload: {
          id,
          comment,
          hashedId,
          purchaserToken,
          confirmationId,
          propertyManagerId,
          statuses,
          currentStatus,
          requesterInfo,
          propertyAddress,
          buildingId,
          buildingUrl,
          buildingAddress,
          buildingType,
          respondingFiles,
          questionnaireFormUrl,
          questionnaireFormPages,
          workflow,
          paidAt,
          autoArchive,
        },
      }
    ) => {
      state.id = id
      state.comment = comment
      state.hashedId = hashedId
      state.purchaserToken = purchaserToken
      state.confirmationId = confirmationId
      state.propertyManagerId = propertyManagerId
      state.statuses = statuses || []
      state.currentStatus = currentStatus
      state.requesterInfo = requesterInfo
      state.propertyAddress = propertyAddress
      state.buildingId = buildingId
      state.buildingUrl = buildingUrl
      state.buildingAddress = buildingAddress
      state.buildingType = buildingType
      state.respondingFiles = respondingFiles
      state.questionnaireForm.url = questionnaireFormUrl || ''
      state.questionnaireForm.pages = questionnaireFormPages || []
      state.paidAt = paidAt
      state.autoArchive = autoArchive

      // Automation or manual states
      state.workflowType = workflow.workflow_type
      state.formInstanceId = workflow.workflow_attributes.form_instance_id

      respondingFiles.forEach(({ id }) => {
        state.attachmentsMap[existingAttachmentKey(id)] = id
      })
    },
    updateStatuses: (state, action) => {
      state.statuses = action.payload
    },
    updateCurrentStatus: (state, action) => {
      state.currentStatus = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addRespondingFile.fulfilled, (state, action) => {
      // Files finish processing in any order; keep the list in selection order
      const pdfFile = { ...action.payload, position: action.meta.arg.position }
      state.respondingFiles = sortAttachments([...state.respondingFiles, pdfFile])
      state.attachmentsMap[action.meta.arg.fileSandboxId] = pdfFile.id
    })
    builder.addCase(removeRespondingFile.fulfilled, (state, action) => {
      const attachmentId = action.meta.arg.attachmentId
      const pdfIndex = state.respondingFiles.findIndex((file) => file.id === attachmentId)
      if (pdfIndex !== -1) {
        state.respondingFiles.splice(pdfIndex, 1)

        const fileSandboxId = action.meta.arg.fileSandboxId
        if (fileSandboxId) {
          delete state.attachmentsMap[fileSandboxId]
        }
      }
    })
    builder.addCase(archiveQuestionnaire.fulfilled, (state, action) => {
      const { current_status, statuses } = action.payload
      state.statuses = statuses
      state.currentStatus = current_status
    })
    builder.addCase(unarchiveQuestionnaire.fulfilled, (state, action) => {
      const { current_status, statuses } = action.payload
      state.statuses = statuses
      state.currentStatus = current_status
    })
  },
})

export const selectCurrentStatus = (state) => state.global.currentStatus
export const selectWorkflowType = (state) => state.global.workflowType

export const { initialInfoGlobal, updateStatuses, updateCurrentStatus } = globalSlice.actions

export default globalSlice.reducer
